import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from 'utils/baseQuery';
import { METHODS } from 'config';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  job: string;
}

interface TrainingCourse {
  _id: string;
  title: string;
  description: string;
  instructor?: string; // Instructor name as string
  startDate: string;
  endDate: string;
  location?: string;
  capacity: number;
  enrolledCount?: number; // Optional, may not always be included
  status: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
  createdAt: string;
  updatedAt: string;
}

interface TrainingEnrollment {
  _id: string;
  course: TrainingCourse;
  user: User;
  enrolledAt: string;
  status: 'enrolled' | 'completed' | 'cancelled';
  attendance: {
    date: string;
    present: boolean;
  }[];
  feedback?: {
    rating: number;
    comment: string;
    submittedAt: string;
  };
}

interface CourseFeedback {
  _id: string;
  user: User;
  rating: number;
  comment: string;
  submittedAt: string;
}

interface TrainingListQuery {
  page?: number;
  limit?: number;
  status?: 'upcoming' | 'ongoing' | 'completed' | 'cancelled';
}

interface TrainingListResponse {
  trainings: TrainingCourse[];
  total: number;
  page: number;
  limit: number;
}

interface CreateTrainingInput {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  location?: string;
  capacity: number;
}

interface MarkAttendanceInput {
  trainingId: string;
  userId: string;
  date: string;
  present: boolean;
}

interface SubmitFeedbackInput {
  trainingId: string;
  rating: number;
  comment: string;
}

const TrainingApi = createApi({
  reducerPath: 'training-api',
  baseQuery: baseQuery(),
  tagTypes: ['Trainings', 'Enrollments', 'Feedback'],
  endpoints: (builder) => ({
    getTrainings: builder.query<TrainingListResponse, TrainingListQuery>({
      query: (params) => ({
        url: '/training/list',
        method: METHODS.get,
        params,
      }),
      providesTags: ['Trainings'],
    }),
    getTraining: builder.query<{ course: TrainingCourse; isEnrolled: boolean; enrolledCount: number }, string>({
      query: (trainingId) => ({
        url: `/training/get`,
        method: METHODS.get,
        params: { trainingId },
      }),
      providesTags: ['Trainings'],
    }),
    createTraining: builder.mutation<{ course: TrainingCourse }, CreateTrainingInput>({
      query: (body) => ({
        url: '/training/create',
        method: METHODS.post,
        body,
      }),
      invalidatesTags: ['Trainings'],
    }),
    enrollTraining: builder.mutation<{ enrollment: TrainingEnrollment }, string>({
      query: (trainingId) => ({
        url: `/training/enroll`,
        method: METHODS.post,
        params: { trainingId },
      }),
      invalidatesTags: ['Trainings', 'Enrollments'],
    }),
    cancelEnrollment: builder.mutation<{ success: boolean }, string>({
      query: (trainingId) => ({
        url: `/training/cancel-enrollment`,
        method: METHODS.post,
        params: { trainingId },
      }),
      invalidatesTags: ['Trainings', 'Enrollments'],
    }),
    getMyEnrollments: builder.query<{ enrollments: TrainingEnrollment[] }, void>({
      query: () => ({
        url: '/training/my-enrollments',
        method: METHODS.get,
      }),
      providesTags: ['Enrollments'],
    }),
    getCourseEnrollments: builder.query<{ enrollments: TrainingEnrollment[] }, string>({
      query: (trainingId) => ({
        url: `/training/enrollments`,
        method: METHODS.get,
        params: { trainingId },
      }),
      providesTags: ['Enrollments'],
    }),
    markAttendance: builder.mutation<{ success: boolean }, MarkAttendanceInput>({
      query: (body) => ({
        url: `/training/mark-attendance`,
        method: METHODS.post,
        body,
      }),
      invalidatesTags: ['Enrollments'],
    }),
    submitFeedback: builder.mutation<{ feedback: CourseFeedback }, SubmitFeedbackInput>({
      query: ({ trainingId, ...body }) => ({
        url: `/training/submit-feedback`,
        method: METHODS.post,
        params: { trainingId },
        body,
      }),
      invalidatesTags: ['Feedback', 'Enrollments'],
    }),
    getCourseFeedback: builder.query<{ data: CourseFeedback[]; averageRating: number }, string>({
      query: (trainingId) => ({
        url: `/training/feedback`,
        method: METHODS.get,
        params: { trainingId },
      }),
      providesTags: ['Feedback'],
    }),
    deleteTraining: builder.mutation<{ success: boolean }, string>({
      query: (trainingId) => ({
        url: `/training/remove`,
        method: METHODS.post,
        params: { trainingId },
      }),
      invalidatesTags: ['Trainings'],
    }),
  }),
});

export const {
  useGetTrainingsQuery,
  useGetTrainingQuery,
  useCreateTrainingMutation,
  useEnrollTrainingMutation,
  useCancelEnrollmentMutation,
  useGetMyEnrollmentsQuery,
  useGetCourseEnrollmentsQuery,
  useMarkAttendanceMutation,
  useSubmitFeedbackMutation,
  useGetCourseFeedbackQuery,
  useDeleteTrainingMutation,
} = TrainingApi;

export { TrainingApi };
export type {
  TrainingCourse,
  TrainingEnrollment,
  CourseFeedback,
  TrainingListQuery,
  CreateTrainingInput,
  MarkAttendanceInput,
  SubmitFeedbackInput,
};
